import React from 'react';
import { Button, ExpandableSection, Toggle, Tooltip } from 'AWS-UI-Components-React';
import { ChannelTypes, ObfuscatedMerchantIdMap, RegionLabelMap, Stage } from '../../constants/spotlight_elements';

import { AccuracyButtonNewStyle, CalculateButtonMargin, DownloadButtonAlign } from '../../styles/common_styles';
import logger from '../../utils/logging';
import { AccuracyButton } from '../buttons/accuracy_button';
import { InputField } from '../common/input_field';

import { getJSClient } from '../js_client';
import { Status } from '../main';

export const defaultPayload = {
  channelType: 'Retail',
  marketplaceId: '',
  merchantCustomerId: '',
  asin: '',

  stage: 'Prod',
  price: '',
  currency: '',
  shippingCost: '',
  listerID: '',
  noCache: true,
  variant: false,
  enableWholesaleProductCost: false,
  getFifoCost: false,
};

export class SpotlightAttributes extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      payload: Object.assign({}, defaultPayload),
      clientLoaded: false,
      formSubmitted: false,
      user: '',
      access: true,
      advancedExpanded: false,
      loadURL: Status.INIT,
      marketplaceIds : []
    };
  }

  setChannelType = (chosen) => {
    const newPayload = Object.assign({}, this.state.payload);
    newPayload.channelType = chosen.detail.selectedId;
    this.setState({payload: newPayload, formSubmitted: false});
  };

  setMarketplaceId = (chosen) => {
    const newPayload = Object.assign({}, this.state.payload);
    newPayload.marketplaceId = chosen.detail.selectedId;
    newPayload.marketplace = parseInt(chosen.detail.selectedId, 10);
    newPayload.merchantCustomerId = ObfuscatedMerchantIdMap[newPayload.marketplaceId];
    this.setState({payload: newPayload, formSubmitted: false});
  };

  setMerchantCustomerId = (input) => {
    const newPayload = Object.assign({}, this.state.payload);
    newPayload.merchantCustomerId = input.detail.value.toUpperCase();
    this.setState({payload: newPayload, formSubmitted: false});
  };

  setAsin = (input) => {
    const newPayload = Object.assign({}, this.state.payload);
    newPayload.asin = input.detail.value.toUpperCase();
    this.setState({payload: newPayload, formSubmitted: false});
  };

  setStage = (chosen) => {
    const newPayload = Object.assign({}, this.state.payload);
    newPayload.stage = chosen.detail.selectedId;
    this.setState({payload: newPayload, formSubmitted: false});
  };

  setOtherMarketplaceId = (input) => {
    const newPayload = Object.assign({}, this.state.payload);
    newPayload.marketplaceId = input.detail.value.toUpperCase();
    this.setState({payload: newPayload, formSubmitted: false});
  };

  setPrice = (input) => {
    const newPayload = Object.assign({}, this.state.payload);
    newPayload.price = input.detail.value;
    this.setState({payload: newPayload, formSubmitted: false});
  };

  setCurrency = (input) => {
    const newPayload = Object.assign({}, this.state.payload);
    newPayload.currency = input.detail.value;
    this.setState({payload: newPayload, formSubmitted: false});
  };

  setShippingCost = (input) => {
    const newPayload = Object.assign({}, this.state.payload);
    newPayload.shippingCost = input.detail.value;
    this.setState({payload: newPayload, formSubmitted: false});
  };

  setListerID = (input) => {
    const newPayload = Object.assign({}, this.state.payload);
    newPayload.listerID = input.detail.value;
    this.setState({payload: newPayload, formSubmitted: false});
  };

  setNoCache = (select) => {
    const newPayload = Object.assign({}, this.state.payload);
    newPayload.noCache = select.detail.checked;
    this.setState({payload: newPayload, formSubmitted: false});
  };

  setVariant = (select) => {
    const newPayload = Object.assign({}, this.state.payload);
    newPayload.variant = select.detail.checked;
    this.setState({payload: newPayload, formSubmitted: false});
  };

  setWholesaleProductCost = (select) => {
    const newPayload = Object.assign({}, this.state.payload);
    newPayload.enableWholesaleProductCost = select.detail.checked;
    this.setState({payload: newPayload, formSubmitted: false});
  };

  setGetFifoCost = (select) => {
    const newPayload = Object.assign({}, this.state.payload);
    newPayload.getFifoCost = select.detail.checked;
    this.setState({payload: newPayload, formSubmitted: false});
  };

  componentDidMount = () => {
    this.client = getJSClient();
    this.initialClientTasks();
  };

  componentDidUpdate = () => {
    if (this.props.loadAsin && this.state.loadURL === Status.INIT) {
      const newPayload = Object.assign({}, this.state.payload);
      newPayload.asin = this.props.loadAsin;
      newPayload.marketplaceId = this.props.loadMarketplaceId;
      newPayload.marketplace = parseInt(this.props.loadMarketplaceId, 10);
      newPayload.merchantCustomerId = this.props.loadMerchantId;
      newPayload.channelType = this.props.loadType;
      this.setState({payload: newPayload, formSubmitted: false}, () => this.submitForm());
      this.setState({formSubmitted: true, loadURL: Status.LOADING});
    }
  };

  initialClientTasks = () => {
    this.getUserIdentity();
    this.client.getMarketplaceConfig(this.onMarketplaceConfigFetchSuccess);
  };

  getUserIdentity = () => {
    console.log('Authenticating User...');
    if (this.props.identity) {
      this.setState({user: this.props.identity.username, clientLoaded: true});
      console.log(`Authenticated user as ${ this.state.user }`);
    } else {
      console.log('No user identity found. Restricting access.');
      this.setState({access: false});
    }
  };

  onMarketplaceConfigFetchSuccess = (response) => {
    try {
      const marketplaceConfigs = JSON.parse(response.marketplaceConfig).filter(mp => mp.spotlightEnabled);
      let regionMap = new Map();
      for (const mp of marketplaceConfigs){
        const regionKey = mp.region;
        if (regionMap.has(regionKey)){
          regionMap.get(regionKey).push({label: mp.detail, id: mp.id});
        } else {
          regionMap.set(regionKey, [{label:mp.detail, id: mp.id}]);
        }
      }
      let marketplaceIds = [];
      for ( let [key, value] of regionMap.entries()){
        marketplaceIds.push({label: RegionLabelMap[key], options: value});
      }
      this.setState({marketplaceIds});
    } catch (e) {
      logger.error('MarketplaceConfig fetch failed', e);
    }
  }

  onAdvancedExpanded = () => {
    if (this.state.advancedExpanded) {
      this.setState({advancedExpanded: false});
    } else {
      this.setState({advancedExpanded: true});
    }
  };

  submitForm = () => {
    logger.info('Submit spotlight payload', this.state.payload);
    this.props.callback(this.state.payload);
    this.props.submitForm();
    this.setState({formSubmitted: true});
    console.log('Form submitted!');
  };

  clearForm = () => {
    this.setState({payload: Object.assign({}, defaultPayload)});
    this.props.resetFunction();
  };

  calculateButtonDisabled() {
    return !this.state.access ||
      !this.state.payload.marketplaceId ||
      !this.state.payload.merchantCustomerId ||
      !this.state.payload.asin;
  }

  render() {
    return (
      <div>
        <AccuracyButton
          text="Channel"
          items={ ChannelTypes }
          onChange={ this.setChannelType }
          selected={ this.state.payload.channelType }
          disabled={ !this.state.access }
        />

        <AccuracyButton
          text="Marketplace"
          items={ this.state.marketplaceIds }
          onChange={ this.setMarketplaceId }
          selected={ this.state.payload.marketplaceId }
          disabled={ !this.state.access }
          required={true}
        />

        <InputField
          text="Merchant Customer ID"
          placeholder={ 'Enter Merchant Customer Id' }
          onChange={ this.setMerchantCustomerId }
          value={ this.state.payload.merchantCustomerId }
          disabled={ !this.state.access }
          required={true}
        />

        <InputField
          text="ASIN"
          placeholder={ 'Enter ASIN' }
          onChange={ this.setAsin }
          value={ this.state.payload.asin }
          disabled={ !this.state.access }
          required={true}
        />

        {this.props.featureList && this.props.featureList.includes('spotlight-variant') &&
        <div className={ AccuracyButtonNewStyle }>
          <Toggle onChange={ this.setVariant } checked={ this.state.payload.variant }>
            Include FC Fixed
          </Toggle>
        </div>}

        {this.props.featureList && this.props.featureList.includes('spotlight-advanced') &&
        <ExpandableSection
          header="Advanced Options"
          variant="borderless"
          expanded={ this.state.advancedExpanded }
          onChange={ this.onAdvancedExpanded }
        >
          <AccuracyButton
            text="Stage"
            items={ Stage }
            onChange={ this.setStage }
            selected={ this.state.payload.stage }
            disabled={ !this.state.access }
          />
          <InputField
            text="Other Marketplace"
            placeholder={ 'Enter Marketplace Id' }
            onChange={ this.setOtherMarketplaceId }
            value={ this.state.payload.marketplaceId }
            disabled={ !this.state.access }
          />
          <InputField
            text="Price"
            placeholder={ 'Enter Price value' }
            onChange={ this.setPrice }
            value={ this.state.payload.price }
            disabled={ !this.state.access }
          />
          <InputField
            text="Currency"
            placeholder={ 'Enter Currency Code' }
            onChange={ this.setCurrency }
            value={ this.state.payload.currency }
            disabled={ !this.state.access }
          />
          <InputField
            text="Shipping Cost"
            placeholder={ 'Enter Shipping Cost' }
            onChange={ this.setShippingCost }
            value={ this.state.payload.shippingCost }
            disabled={ !this.state.access }
          />
          <InputField
            text="Lister ID"
            placeholder={ 'Enter Lister Id' }
            onChange={ this.setListerID }
            value={ this.state.payload.listerID }
            disabled={ !this.state.access }
          />
          <div className={ AccuracyButtonNewStyle }>
            <Toggle onChange={ this.setNoCache } checked={ this.state.payload.noCache }>
              noCache
            </Toggle>
            <Toggle onChange={ this.setWholesaleProductCost } checked={ this.state.payload.enableWholesaleProductCost }>
              enableWholesaleProductCost
            </Toggle>
            <Toggle onChange={ this.setGetFifoCost } checked={ this.state.payload.getFifoCost }>
              getFifoCost
            </Toggle>
          </div>
        </ExpandableSection>}
        <div className={ DownloadButtonAlign }>
          <Button text="Clear" variant="normal" onClick={ this.clearForm } disabled={ !this.state.access } />
          <Tooltip className={ CalculateButtonMargin } text="Required Input: Asin, MarketplaceId and MerchantCustomerId" position="top">
            <Button
              text="Calculate"
              variant="primary"
              onClick={ this.submitForm }
              disabled={ this.calculateButtonDisabled() }
              loading={ this.props.loading }
            />
          </Tooltip>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/spotlight/spotlight_attributes.jsx